// Web Crypto API ベースの SHA-256 ヘルパー。
//
// Secret Chat ではパスワード平文をサーバへ送らない (Requirements R1.3, R8.1)。
// 代わりにブラウザ側で SHA-256 ハッシュをとり、その 64 桁 hex 文字列を
// roomId として使用する。本モジュールはその基本処理を提供する。
//
// 依存: ブラウザ標準の Web Crypto API (`crypto.subtle.digest`) と
//       `TextEncoder` のみ。外部ライブラリには依存しない。

/**
 * UTF-8 として `text` をエンコードし、SHA-256 ハッシュの 16 進数文字列
 * (lowercase, 64 文字) を返す。
 *
 * - 同一入力に対する出力は安定（決定的）。
 * - 戻り値は常に長さ 64 の `[0-9a-f]+` 文字列。
 *
 * @param text ハッシュ化したい任意の UTF-8 文字列（パスワードなど）
 * @returns 64 桁の小文字 16 進数文字列
 */
export async function sha256Hex(text: string): Promise<string> {
  // 1. 入力を UTF-8 バイト列に変換する。
  //    `TextEncoder` は常に UTF-8 を使うため roomId は端末非依存で再現可能。
  const encoded = new TextEncoder().encode(text);

  // 2. Web Crypto API の SHA-256 ダイジェストを取得する。
  //    `crypto.subtle` はセキュアコンテキスト (https または localhost) で利用可能。
  const digest = await crypto.subtle.digest('SHA-256', encoded);

  // 3. ArrayBuffer を 1 バイトずつ 2 桁 hex に変換し連結する (合計 32 バイト → 64 文字)。
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}
